/**
 * Shared PayPal SDK loader for the checkout surfaces.
 *
 * Two hard-won rules live here:
 * 1. NEVER re-evaluate the SDK script when the same src is already loaded
 *    (e.g. the PayPal section remounts after a plan switch): the second
 *    bootstrap crashes zoid with "Request listener already exists for
 *    zoid_allow_delegate_paypal_buttons" and the buttons never render.
 *    Reuse window.paypal and just re-render.
 * 2. When a DIFFERENT SDK is genuinely needed (currency change, or the
 *    one-time intent vs the vault/subscription intent), remove EVERY
 *    previously loaded variant AND the zoid/post-robot window globals the
 *    old SDK left behind — they are what trigger the listener collision.
 */

const SDK_IDS = ["paypal-sdk", "paypal-sdk-sub"];

export function loadPayPalSdk(
  scriptId: string,
  src: string,
  onReady: () => void,
  onError?: () => void,
): void {
  const w = window as unknown as Record<string, unknown>;
  const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (existing && existing.src === src && w.paypal) {
    onReady();
    return;
  }

  SDK_IDS.forEach((id) => document.getElementById(id)?.remove());
  try {
    delete w.paypal;
  } catch {
    /* ignore */
  }
  // zoid (the SDK's iframe broker) registers post-robot listeners on window
  // globals that survive script removal — clean them so the fresh SDK can
  // bootstrap its own set.
  Object.keys(w).forEach((k) => {
    if (k.startsWith("__post_robot") || k.startsWith("__zoid")) {
      try {
        delete w[k];
      } catch {
        /* ignore */
      }
    }
  });

  const script = document.createElement("script");
  script.id = scriptId;
  script.src = src;
  script.onload = onReady;
  // Ad-blockers and in-app browsers can block the SDK request entirely. Remove
  // the dead tag (so a later call can retry a fresh load) and tell the caller,
  // which otherwise sits on an eternal "Loading PayPal..." placeholder.
  script.onerror = () => {
    script.remove();
    onError?.();
  };
  document.body.appendChild(script);
}
